import { useEffect, useState, useMemo } from "react";
import { 
  Droplets, Activity, Percent, Settings, 
  Wallet, Users, TrendingUp, Briefcase, 
  List, TrendingDown, X
} from "lucide-react";
import { useIsMobile } from "@/hooks/useIsMobile";
import { usePumpPortalTrades } from "@/hooks/usePumpPortalTrades";
import { fetchNativeSolBalance, fetchSplTokenBalance } from "@/lib/solanaBalance";
import { fetchTokenUiSupply } from "@/lib/solanaTokenSupply";
import { useWallet } from "@/context/WalletContext";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { fetchHeliusAssetPrice } from "@/lib/heliusDas";

interface LiquidityPoolToolbarProps {
  mint: string | null;
  liveLiqSol?: number | null;
  liveVolSol?: number | null;
  solPrice: number;
}

interface OpenPosition {
  mint: string | null;
  size: number;
  entryPriceSol: number;
  currentPriceSol: number;
  valueUsd: number;
  pnlUsd: number;
  pnlPercent: number;
}

interface WatchlistPosition extends OpenPosition {
  trader: string;
  netSol: number;
  tradeCount: number;
  direction: "LONG" | "SHORT";
  lastSeen: number;
}

export function LiquidityPoolToolbar({ mint, liveLiqSol, liveVolSol, solPrice }: LiquidityPoolToolbarProps) {
  const isMobile = useIsMobile();
  const { t, locale } = useTranslation();
  const { rows: pumpPortalTrades, state: pumpPortalState } = usePumpPortalTrades(mint);
  
  const [isExpanded, setIsExpanded] = useState(!isMobile);
  
  const { connected, publicKey } = useWallet();
  
  // Balances & Helius Fallbacks
  const [solBalance, setSolBalance] = useState<number | null>(null);
  const [tokenBalance, setTokenBalance] = useState<number | null>(null);
  const [tokenSupply, setTokenSupply] = useState<number | null>(null);
  const [heliusPriceSol, setHeliusPriceSol] = useState<number | null>(null);

  // Fetch initial balances
  useEffect(() => {
    if (!publicKey || !connected) return;
    fetchNativeSolBalance(publicKey).then(setSolBalance);
    if (mint) {
      fetchSplTokenBalance(publicKey, mint).then(setTokenBalance);
      fetchHeliusAssetPrice(mint).then((p) => {
        if (p?.pricePerToken && solPrice > 0) {
          setHeliusPriceSol(p.pricePerToken / solPrice);
        }
      });
    }
    
    const interval = setInterval(() => {
      fetchNativeSolBalance(publicKey).then(setSolBalance);
      if (mint) {
        fetchSplTokenBalance(publicKey, mint).then(setTokenBalance);
        fetchHeliusAssetPrice(mint).then((p) => {
          if (p?.pricePerToken && solPrice > 0) {
            setHeliusPriceSol(p.pricePerToken / solPrice);
          }
        });
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [publicKey, connected, mint]);

  useEffect(() => {
    if (!mint) {
      setTokenSupply(null);
      return;
    }
    let active = true;
    void fetchTokenUiSupply(mint).then((supply) => {
      if (active) setTokenSupply(supply);
    });
    return () => {
      active = false;
    };
  }, [mint]);

  const [localLimitOrders, setLocalLimitOrders] = useState<any[]>([]);

  const fetchLocalLimitOrders = async () => {
    try {
      const res = await fetch("/api/limit-orders", { headers: { "x-lily-app": "1" } });
      const data = await res.json();
      if (Array.isArray(data)) {
        setLocalLimitOrders(data.filter((o: any) => o.mint === mint));
      }
    } catch {}
  };

  useEffect(() => {
    if (mint) {
      fetchLocalLimitOrders();
    }
  }, [mint]);

  const handleCancelLimitOrder = async (id: string) => {
    try {
      const res = await fetch(`/api/limit-orders/${id}`, { method: "DELETE", headers: { "x-lily-app": "1" } });
      if (res.ok) {
        fetchLocalLimitOrders();
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Calculate real-time market and user position statistics
  const { stats, myPosition, currentPrices } = useMemo(() => {
    let currentTokenPriceSol = 0;
    if (pumpPortalTrades.length > 0) {
      const latest = pumpPortalTrades[0];
      if (latest.tokenAmount && latest.tokenAmount > 0) {
        currentTokenPriceSol = latest.sol / latest.tokenAmount;
      }
    }
    // Fallback to Helius price if websocket has no trades yet
    if (currentTokenPriceSol === 0 && heliusPriceSol !== null) {
      currentTokenPriceSol = heliusPriceSol;
    }

    let totalBoughtTokens = 0;
    let totalSoldTokens = 0;
    let totalSpentSol = 0;
    let totalReceivedSol = 0;

    if (publicKey) {
      const myTrades = pumpPortalTrades.filter(t => t.trader === publicKey.toString());
      myTrades.forEach(t => {
        if (t.buy) {
          totalBoughtTokens += (t.tokenAmount || 0);
          totalSpentSol += (t.sol || 0);
        } else {
          totalSoldTokens += (t.tokenAmount || 0);
          totalReceivedSol += (t.sol || 0);
        }
      });
    }

    const currentTokenValueUsd = (tokenBalance || 0) * currentTokenPriceSol * solPrice;
    
    // Average entry calculation
    const averageEntrySol = totalBoughtTokens > 0 ? (totalSpentSol / totalBoughtTokens) : 0;
    
    // Unrealized PNL based on remaining tokens
    const currentPriceUsd = currentTokenPriceSol * solPrice;
    const entryPriceUsd = averageEntrySol * solPrice;
    const pnlUsd = (tokenBalance || 0) > 0 ? (currentPriceUsd - entryPriceUsd) * (tokenBalance || 0) : 0;
    const pnlPercent = entryPriceUsd > 0 ? ((currentPriceUsd - entryPriceUsd) / entryPriceUsd) * 100 : 0;

    // Pump stats processing
    const recentTrades = pumpPortalTrades.slice(0, 50);
    const buys = recentTrades.filter(t => t.buy).length;
    const total = recentTrades.length;
    const buyPressure = total > 0 ? (buys / total) * 100 : 50;
    const totalSolVol = recentTrades.reduce((sum, t) => sum + t.sol, 0);

    let latestMc = recentTrades.find(t => t.mcUsd !== null)?.mcUsd || 0;
    
    // Fallback Mkt Cap using Helius price * supply
    if (latestMc === 0 && currentTokenPriceSol > 0 && tokenSupply != null) {
      latestMc = (currentTokenPriceSol * solPrice) * tokenSupply;
    }
    
    const mcSol = latestMc > 0 ? latestMc / solPrice : (liveLiqSol || 0);
    const bondingCurveProgress = Math.min((mcSol / 85) * 100, 100); // Pump.fun ~85 SOL target

    return {
      stats: {
        liquiditySol: mcSol,
        liquidityUsd: latestMc,
        volume24h: totalSolVol * solPrice,
        transactions24h: pumpPortalTrades.length,
        buyPressure,
        bondingCurveProgress,
        lastUpdate: Date.now(),
      },
      myPosition: {
        totalBought: totalBoughtTokens,
        totalSold: totalSoldTokens,
        netSol: totalReceivedSol - totalSpentSol,
        remainingTokens: tokenBalance || 0,
        currentValueUsd: currentTokenValueUsd,
        averageEntrySol,
        pnlUsd,
        pnlPercent
      },
      currentPrices: {
        sol: currentTokenPriceSol,
        usd: currentPriceUsd
      }
    };
  }, [mint, pumpPortalTrades, liveLiqSol, liveVolSol, solPrice, publicKey, tokenBalance, heliusPriceSol, tokenSupply]);

  const walletTokenValueUsd = tokenBalance != null ? tokenBalance * currentPrices.sol * solPrice : 0;
  const lastTradeTime = pumpPortalTrades[0]?.ts
    ? new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).format(new Date(pumpPortalTrades[0].ts))
    : "—";

  // Derive Open Positions List
  const openPositions = useMemo<OpenPosition[]>(() => {
    if (myPosition.remainingTokens <= 0) return [];
    return [
      {
        mint,
        size: myPosition.remainingTokens,
        entryPriceSol: myPosition.averageEntrySol,
        currentPriceSol: currentPrices.sol,
        valueUsd: myPosition.currentValueUsd,
        pnlUsd: myPosition.pnlUsd,
        pnlPercent: myPosition.pnlPercent,
      },
    ];
  }, [myPosition, mint, currentPrices]);

  const watchlistPositions = useMemo<WatchlistPosition[]>(() => {
    const traders = new Map<string, {
      trader: string;
      netTokens: number;
      boughtTokens: number;
      spentSol: number;
      receivedSol: number;
      tradeCount: number;
      lastSeen: number;
    }>();

    for (const trade of pumpPortalTrades) {
      if (!trade.trader) continue;
      if (publicKey && trade.trader === publicKey.toString()) continue;
      const tokenAmount = trade.tokenAmount || 0;
      if (tokenAmount === 0) continue;

      const summary = traders.get(trade.trader) ?? {
        trader: trade.trader,
        netTokens: 0,
        boughtTokens: 0,
        spentSol: 0,
        receivedSol: 0,
        tradeCount: 0,
        lastSeen: 0,
      };

      if (trade.buy) {
        summary.netTokens += tokenAmount;
        summary.boughtTokens += tokenAmount;
        summary.spentSol += trade.sol;
      } else {
        summary.netTokens -= tokenAmount;
        summary.receivedSol += trade.sol;
      }

      summary.tradeCount += 1;
      summary.lastSeen = Math.max(summary.lastSeen, trade.ts);
      traders.set(trade.trader, summary);
    }

    return Array.from(traders.values())
      .filter((summary) => Math.abs(summary.netTokens) > 0)
      .map((summary) => {
        const direction: "LONG" | "SHORT" = summary.netTokens > 0 ? "LONG" : "SHORT";
        const valueUsd = summary.netTokens * currentPrices.sol * solPrice;
        const costBasisUsd = (summary.spentSol - summary.receivedSol) * solPrice;
        const pnlUsd = valueUsd - costBasisUsd;
        const pnlPercent = costBasisUsd !== 0 ? (pnlUsd / Math.abs(costBasisUsd)) * 100 : 0;

        return {
          trader: summary.trader,
          mint,
          size: summary.netTokens,
          entryPriceSol: summary.boughtTokens > 0 ? summary.spentSol / summary.boughtTokens : 0,
          currentPriceSol: currentPrices.sol,
          valueUsd,
          pnlUsd,
          pnlPercent,
          netSol: summary.spentSol - summary.receivedSol,
          tradeCount: summary.tradeCount,
          direction,
          lastSeen: summary.lastSeen,
        };
      })
      .sort((a, b) => b.lastSeen - a.lastSeen)
      .slice(0, 5);
  }, [pumpPortalTrades, publicKey, currentPrices, solPrice, mint]);


  // Actions

  const formatNumber = (n: number, decimals = 2) => {
    if (n == null || isNaN(n)) return "0";
    if (n >= 1_000_000) return `${(n / 1_000_000).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}M`;
    if (n >= 1_000) return `${(n / 1_000).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}K`;
    return `${n.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  };
  const formatUsd = (n: number, decimals = 2) => `$${formatNumber(n, decimals)}`;
  const formatSol = (n: number) => {
    if (n == null || isNaN(n)) return "0.000 SOL";
    return `${n.toLocaleString("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} SOL`;
  };

  if (!mint) return null;

  return (
    <div className={`transition-all duration-500 flex flex-col ${isExpanded ? "h-auto" : "h-12"} ${isMobile ? "m-2 mb-4 rounded-xl border border-white/10 bg-neutral-900/95 shadow-lg" : "border-t border-white/10 bg-neutral-900"} backdrop-blur-xl`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/5 shrink-0">
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20`}>
            <Droplets className="size-4 text-blue-400" />
            <span className="text-[10px] font-black uppercase tracking-widest text-blue-400">{t("panel.pumpLiquidity")}</span>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-white/40">
            <Activity className={`size-3 ${pumpPortalState === "open" ? "text-emerald-400 animate-pulse" : "text-amber-400"}`} />
            <span>{pumpPortalState === "open" ? t("panel.live") : pumpPortalState === "connecting" ? t("panel.connecting") : t("panel.offline")}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-1.5 px-2 py-1 rounded bg-white/5 hover:bg-white/10 transition-all text-white/60 hover:text-white"
          >
            <Settings className="size-3" />
            <span className="text-[9px] font-bold uppercase">{isExpanded ? t("panel.collapse") : t("panel.expand")}</span>
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className={`p-3 bg-black/20 overflow-y-auto`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-3">
            <StatCard
              icon={<Wallet className="size-3 text-emerald-400" />}
              label={t("panel.walletSol")}
              value={solBalance != null ? formatSol(solBalance) : "--"}
              subvalue={lastTradeTime ? t("panel.updatedAt", { time: lastTradeTime }) : ""}
            />
            <StatCard
              icon={<Droplets className="size-3 text-blue-400" />}
              label={t("panel.tokenBalance")}
              value={tokenBalance != null ? formatNumber(tokenBalance) : "--"}
              subvalue={tokenBalance != null ? formatUsd(walletTokenValueUsd) : ""}
            />
            <StatCard
              icon={<TrendingUp className="size-3 text-cyan-400" />}
              label={t("panel.marketPrice")}
              value={currentPrices.sol > 0 ? `${currentPrices.sol.toFixed(6)} SOL` : "--"}
              subvalue={currentPrices.sol > 0 ? formatUsd(currentPrices.usd) : ""}
            />
            <StatCard
              icon={<Percent className="size-3 text-white" />}
              label={t("panel.tokenSupply")}
              value={tokenSupply != null ? formatNumber(tokenSupply, 0) : "—"}
              subvalue={tokenSupply != null ? t("panel.onChainSupply") : t("panel.loading")}
            />
          </div>

          <div className={`grid grid-cols-1 ${isMobile ? "gap-4" : "lg:grid-cols-2 gap-3"} mb-3`}>

            {/* MY POSITION */}
            <div className="bg-black/40 border border-white/10 rounded-xl p-3 flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <Briefcase className="size-4 text-purple-400" />
                <span className="text-[12px] font-black uppercase text-purple-400">{t("panel.myPosition")}</span>
              </div>
              <div className="flex-1 flex flex-col justify-around gap-2">
                <div className="flex justify-between items-center bg-white/5 rounded p-2 border border-white/5">
                  <span className="text-[10px] text-white/50 uppercase font-bold">{t("pool.unsoldValue")}</span>
                  <div className="text-right">
                    <div className="text-[12px] font-mono text-white font-bold">{formatNumber(myPosition.remainingTokens)}</div>
                    <div className="text-[10px] font-mono text-emerald-400">{formatUsd(myPosition.currentValueUsd)}</div>
                  </div>
                </div>
                <div className="flex justify-between items-center px-1">
                  <span className="text-[10px] text-white/50 uppercase font-bold">{t("pool.totalBought")}</span>
                  <span className="text-[11px] font-mono text-white">{formatNumber(myPosition.totalBought)}</span>
                </div>
                <div className="flex justify-between items-center px-1">
                  <span className="text-[10px] text-white/50 uppercase font-bold">{t("pool.totalSold")}</span>
                  <span className="text-[11px] font-mono text-white">{formatNumber(myPosition.totalSold)}</span>
                </div>
                <div className="flex justify-between items-center px-1">
                  <span className="text-[10px] text-white/50 uppercase font-bold">{t("pool.netPnl")}</span>
                  <span className={`text-[11px] font-mono font-bold ${myPosition.netSol >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                    {myPosition.netSol > 0 ? "+" : ""}{myPosition.netSol.toFixed(3)}
                  </span>
                </div>
              </div>
            </div>

            {/* POOL STATS */}
            <div className="grid grid-cols-2 grid-rows-2 gap-2">
              <StatCard icon={<Droplets className="size-3 text-blue-400" />} label={t("pool.mktCap")} value={formatUsd(stats.liquidityUsd)} subvalue={formatSol(stats.liquiditySol)} />
              <StatCard icon={<TrendingUp className="size-3 text-emerald-400" />} label={t("pool.curve")} value={`${stats.bondingCurveProgress.toFixed(1)}%`} showBar barValue={stats.bondingCurveProgress} />
              <StatCard icon={<Activity className="size-3 text-purple-400" />} label="Volume (24h)" value={formatUsd(stats.volume24h)} subvalue={`${stats.transactions24h} txs`} />
              <StatCard icon={<Percent className="size-3 text-cyan-400" />} label="Buy Pressure" value={`${stats.buyPressure.toFixed(0)}%`} showBar barValue={stats.buyPressure} />
            </div>

          </div>

          {/* ACTIVE LIMIT ORDERS */}
          {localLimitOrders.length > 0 && (
            <div className="border border-white/10 bg-black/60 rounded-xl overflow-hidden mt-3">
              <div className="px-4 py-3 border-b border-white/5 bg-white/[0.02] flex items-center gap-2">
                <List className="size-4 text-purple-400" />
                <h3 className="text-[11px] font-black uppercase tracking-widest text-purple-400">Active Limit Orders</h3>
              </div>
              <div className="overflow-x-auto max-h-48 overflow-y-auto">
                <table className="w-full text-left text-xs whitespace-nowrap">
                  <thead className="bg-black/40 text-[9px] uppercase text-white/40 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 font-bold">Action</th>
                      <th className="px-4 py-2 font-bold">Amount</th>
                      <th className="px-4 py-2 font-bold">Trigger</th>
                      <th className="px-4 py-2 font-bold text-center">Cancel</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {localLimitOrders.map((order) => (
                      <tr key={order.id} className="hover:bg-white/5 transition-colors">
                        <td className="px-4 py-2">
                          <span className={`font-bold uppercase ${order.action === 'buy' ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {order.action}
                          </span>
                        </td>
                        <td className="px-4 py-2 font-mono text-white/80">{order.amount}</td>
                        <td className="px-4 py-2 font-mono text-cyan-300">
                          {order.triggerValue} {order.triggerType === "marketcap" ? "MCap" : "SOL"}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button 
                            onClick={() => handleCancelLimitOrder(order.id)} 
                            className="p-1 rounded hover:bg-rose-500/20 text-rose-400/50 hover:text-rose-400 transition-colors mx-auto"
                          >
                            <X className="size-3" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* OPEN POSITIONS TABLE */}
          <div className="border border-white/10 bg-black/60 rounded-xl overflow-hidden mt-3">
            <div className="px-3 py-2 border-b border-white/5 bg-white/[0.02] flex items-center gap-2">
              <List className="size-4 text-white/50" />
              <h3 className="text-[11px] font-black uppercase tracking-widest text-white/80">{t("panel.openPositions")}</h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs whitespace-nowrap">
                <thead className="bg-black/40 text-[9px] uppercase text-white/40">
                  <tr>
                    <th className="px-4 py-3 font-bold">Asset</th>
                    <th className="px-4 py-3 font-bold text-right">Size (Tokens)</th>
                    <th className="px-4 py-3 font-bold text-right">Avg Entry</th>
                    <th className="px-4 py-3 font-bold text-right">Current Price</th>
                    <th className="px-4 py-3 font-bold text-right">Value (USD)</th>
                    <th className="px-4 py-3 font-bold text-right">Unrealized PNL</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {openPositions.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-white/30 text-xs">
                        {t("panel.noPositions")}
                      </td>
                    </tr>
                  ) : (
                    openPositions.map((pos, i) => {
                      const isProfit = pos.pnlUsd >= 0;
                      return (
                        <tr key={i} className="hover:bg-white/5 transition-colors group">
                          <td className="px-4 py-3">
                            <div className="flex flex-col">
                              <span className="font-mono text-white font-bold">{pos.mint?.slice(0, 4)}...{pos.mint?.slice(-4)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-white/80">{formatNumber(pos.size)}</td>
                          <td className="px-4 py-3 text-right font-mono text-white/60">{pos.entryPriceSol.toFixed(8)} SOL</td>
                          <td className="px-4 py-3 text-right font-mono text-white/60">{pos.currentPriceSol.toFixed(8)} SOL</td>
                          <td className="px-4 py-3 text-right font-mono font-bold text-white">{formatUsd(pos.valueUsd)}</td>
                          <td className="px-4 py-3 text-right">
                            <div className={`flex items-center justify-end gap-1 font-mono font-bold ${isProfit ? "text-emerald-400" : "text-rose-400"}`}>
                              {isProfit ? <TrendingUp className="size-3" /> : <TrendingDown className="size-3" />}
                              {isProfit ? "+" : ""}{formatUsd(pos.pnlUsd)} ({pos.pnlPercent.toFixed(2)}%)
                            </div>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {watchlistPositions.length > 0 && (
            <div className="border border-white/10 bg-black/60 rounded-xl overflow-hidden mt-4">
              <div className="px-4 py-3 border-b border-white/5 bg-white/[0.02] flex items-center gap-2">
                <Users className="size-4 text-white/50" />
                <h3 className="text-[11px] font-black uppercase tracking-widest text-white/80">{t("panel.otherWallets")}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs whitespace-nowrap">
                  <thead className="bg-black/40 text-[9px] uppercase text-white/40">
                    <tr>
                      <th className="px-4 py-3 font-bold">Wallet</th>
                      <th className="px-4 py-3 font-bold text-right">Net Size</th>
                      <th className="px-4 py-3 font-bold text-right">Direction</th>
                      <th className="px-4 py-3 font-bold text-right">Value</th>
                      <th className="px-4 py-3 font-bold text-right">P&L</th>
                      <th className="px-4 py-3 font-bold text-right">Seen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {watchlistPositions.map((pos, i) => {
                      const isProfit = pos.pnlUsd >= 0;
                      return (
                        <tr key={i} className="hover:bg-white/5 transition-colors group">
                          <td className="px-4 py-3 font-mono text-white/80">{pos.trader.slice(0, 4)}...{pos.trader.slice(-4)}</td>
                          <td className="px-4 py-3 text-right font-mono text-white/80">{formatNumber(Math.abs(pos.size))}</td>
                          <td className={`px-4 py-3 text-right font-bold uppercase ${pos.direction === "LONG" ? "text-emerald-400" : "text-rose-400"}`}>{pos.direction}</td>
                          <td className="px-4 py-3 text-right font-mono font-bold text-white">{formatUsd(pos.valueUsd)}</td>
                          <td className={`px-4 py-3 text-right font-mono font-bold ${isProfit ? "text-emerald-400" : "text-rose-400"}`}>{isProfit ? "+" : ""}{formatUsd(pos.pnlUsd)} ({pos.pnlPercent.toFixed(1)}%)</td>
                          <td className="px-4 py-3 text-right text-white/50">{new Date(pos.lastSeen).toLocaleTimeString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Removed FundingWallet Modal */}
    </div>
  );
}

// -------------------------------------------------------------
// UI Helper Components
// -------------------------------------------------------------
interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  subvalue?: string;
  showBar?: boolean;
  barValue?: number;
}

function StatCard({ icon, label, value, subvalue, showBar, barValue }: StatCardProps) {
  return (
    <div className={`relative overflow-hidden rounded-xl bg-white/[0.02] border border-white/5 p-2 flex flex-col justify-between transition-all duration-300`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <div className="p-1 rounded bg-black/40 border border-white/5">{icon}</div>
        <span className="text-[9px] font-black uppercase tracking-widest text-white/50">{label}</span>
      </div>
      <div className="flex flex-col">
        <span className="text-xs font-mono font-black text-white leading-none">{value}</span>
        {subvalue && <span className="text-[8px] font-bold text-white/40 mt-1">{subvalue}</span>}
      </div>
      {showBar && barValue !== undefined && (
        <div className="mt-1.5 h-1 w-full rounded-full bg-black/40 overflow-hidden border border-white/5">
          <div
            className={`h-full ${barValue > 50 ? "bg-[#00FFA3]" : "bg-[#FF3358]"}`}
            style={{ width: `${Math.min(Math.max(barValue, 0), 100)}%` }}
          />
        </div>
      )}
    </div>
  );
}